import { Check, ChevronDown, Search, X } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { Region } from "../types";

type Role = "origin" | "destination";

interface RegionPickerProps {
  role: Role;
  regions: Region[];
  values: string[];
  onChange: (values: string[]) => void;
}

export function RegionPicker({ role, regions, values, onChange }: RegionPickerProps) {
  const id = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const results = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    return regions
      .filter((region) => !normalized || `${region.shortName} ${region.name} ${region.group}`.toLocaleLowerCase().includes(normalized))
      .sort((left, right) => left.shortName.localeCompare(right.shortName));
  }, [query, regions]);

  const summary = values.length === 0
    ? `All ${role === "origin" ? "origins" : "destinations"}`
    : values.length === 1
      ? regions.find((region) => region.name === values[0])?.shortName ?? values[0]
      : `${values.length} zones`;

  const toggle = (name: string) => {
    onChange(values.includes(name) ? values.filter((value) => value !== name) : [...values, name]);
  };

  return (
    <div className={`region-picker role-${role}`} ref={rootRef}>
      <div className="field-label-row">
        <label id={`${id}-label`}>{role === "origin" ? "Origin" : "Destination"}</label>
        {values.length > 0 && <button className="text-button" type="button" onClick={() => onChange([])}>Clear</button>}
      </div>
      <button
        className="region-picker-trigger"
        type="button"
        aria-labelledby={`${id}-label`}
        aria-expanded={open}
        aria-controls={`${id}-popover`}
        onClick={() => setOpen((value) => !value)}
      >
        <span className={`role-marker ${role}`}>{role === "origin" ? "O" : "D"}</span>
        <span className="picker-summary">{summary}</span>
        <ChevronDown aria-hidden="true" />
      </button>

      {open && (
        <div className="region-picker-popover" id={`${id}-popover`} role="dialog" aria-label={`Choose ${role} zones`}>
          <div className="picker-search">
            <Search aria-hidden="true" />
            <input autoFocus aria-label={`Search ${role} zones`} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search Chennai zones" />
            {query && <button className="icon-button compact" type="button" aria-label="Clear search" onClick={() => setQuery("")}><X aria-hidden="true" /></button>}
          </div>
          <div className="picker-results">
            {results.length === 0 ? <p className="empty-message">No matching zone</p> : results.map((region) => {
              const selected = values.includes(region.name);
              return (
                <button type="button" className={selected ? "is-selected" : ""} key={region.name} aria-pressed={selected} onClick={() => toggle(region.name)}>
                  <span className="picker-check">{selected && <Check aria-hidden="true" />}</span>
                  <span><strong>{region.shortName}</strong><small>{region.group}{region.isAirport ? " · airport" : ""}</small></span>
                </button>
              );
            })}
          </div>
          <div className="picker-footer">
            <span>{values.length ? `${values.length} selected` : "All zones included"}</span>
            <button className="button secondary compact" type="button" onClick={() => setOpen(false)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}
